<template>
  <div class="staff pt-6">
    <div class="flex flex-wrap -mx-4">
      <div class="w-12/12 md:w-5/12 3xl:w-4/12 px-4">
        <div class="bg-blue-100 rounded-2xl px-6 py-12 relative text-center tourleader-intro">
          <div class="m-auto tourleader-intro__avatar">
            <div class="aspect-4/4">
              <div class="size-full rounded-full overflow-clip relative">
                <ResponsiveImage
                  position="absolute"
                  class="size-full"
                  :image="info?.picture_cloudinary"
                  auto-size
                  :alt="name"
                  auto-gravity
                />
              </div>
            </div>
          </div>
          <h3 class="tourleader-name pt-4 pb-4">
            {{ name }}
          </h3>
          <div
            v-if="video"
            class="text-medium-blue"
            role="button"
            :title="$t('tourleader.video.personal', { name })"
            data-i18n="tourleader.video.personal"
            @click="showSingleVideo"
          >
            <fa
              class="mr-2"
              icon="video"
            />
            <span class="font-bold">
              {{ $t('tourleader.video.personal', { name }) }}
            </span>
          </div>
        </div>
      </div>
      <div class="w-12/12 md:w-7/12 3xl:w-8/12 px-4 md:pl-5 py-8 md:pt-0">
        <template v-if="content">
          <h4
            data-i18n="tourleader.gettoknow"
            class="!font-bold text-2xl"
          >
            {{ $t('tourleader.gettoknow') }}
          </h4>
          <LazyMachineTranslatedReviewsNotice v-if="locale !== 'da'">
            <span data-i18n="tourleaderPage.translatedFromSwedish">
              {{ $t('tourleaderPage.translatedFromSwedish') }}
            </span>
          </LazyMachineTranslatedReviewsNotice>
          <p
            class="whitespace-pre-line staff-text font-secondary"
            :data-translation="info?.info_translationkey"
            v-html="content"
          />
        </template>
        <div class="mt-4">
          <nuxt-link
            v-for="(trip, idx) in trips"
            :key="`trip-${idx}`"
            class="bg-dirty-blue hover:bg-gray-50 font-secondary text-base float-left text-blue-800 font-semibold border border-transparent hover:border-blue-800 mr-2 mb-2 px-4 text-nowrap py-1 rounded-[1.25rem]"
            :to="`/${trip.url}`"
          >
            {{ trip.title }}
          </nuxt-link>
        </div>
      </div>
    </div>
    <div class="flex flex-col">
      <div
        v-if="lectures.length > 0"
        class="text-center staff-videos mt-12 mb-4 flex flex-wrap -mx-4"
      >
        <div
          v-for="(vid, idx) in lectures"
          :key="`lecture-${idx}`"
          class="w-12/12"
          :class="[lectureColClass]"
        >
          <LazyImageCardWithHeadline
            class="my-2 mx-0 tourleader-video"
            role="button"
            :image="vid?.main_picture_cloudinary"
          >
            <div class="absolute bottom-0 left-0 px-4 py-4 text-white">
              <div
                class="article-block--title drop-shadow-medium"
                data-i18n="tourleader.video.cta"
              >
                {{ $t('tourleader.video.cta', { name: vid?.name }) }}
              </div>
            </div>
            <div
              class="absolute top-0 left-0 size-full flex-row flex items-center justify-center"
              @click="showMediaModal(vid)"
            >
              <i-play-button
                class="play-button preview-play-button"
                filled
                :font-controlled="false"
                alt="play"
              />
            </div>
          </LazyImageCardWithHeadline>
        </div>
      </div>
      <div
        v-if="hasQuestions || hasReviews"
        class="flex flex-wrap -mx-4 py-12"
      >
        <div
          v-if="hasQuestions"
          class="w-12/12 md:w-5/12 3xl:w-4/12 px-4"
        >
          <LazyTourleaderQuestions :questions="questions" />
        </div>
        <div
          v-if="hasReviews"
          class="w-12/12 md:pl-5 px-4"
          :class="[hasQuestions ? 'md:w-7/12 3xl:w-8/12' : 'py-4']"
        >
          <LazyTourleaderReviewSummary
            :id="id"
            :reviews="reviewList"
            :full-width="!hasQuestions"
          >
            <template #title>
              <span
                class="text-2xl font-bold"
                data-i18n="tourleaderReviewTitle"
              >
                {{ $t('tourleaderReviewTitle', { name }) }}
              </span>
            </template>
          </LazyTourleaderReviewSummary>
        </div>
      </div>
    </div>
    <LazyTourleaderCalendar :id="id">
      <template #title>
        <span
          data-i18n="tourleaderCalendarTitle"
          class="font-bold text-2xl"
        >
          {{ $t('tourleaderCalendarTitle', { name }) }}
        </span>
      </template>
    </LazyTourleaderCalendar>
    <LazyMediaModal
      v-if="showModal"
      :visible="showModal"
      :media-list="mediaModal?.video"
      :title="mediaModal?.title"
      size="lg"
      hide-header
      centered
      show-close
      aspect-ratio="4/4"
      @on-hide="hideMediaModal"
    />
  </div>
</template>

<script>
import { mapState } from 'pinia'

export default defineNuxtComponent({
  props: {
    id: {
      type: String,
      default: '',
    },

    info: {
      type: Object,
      default: () => ({
        name: null,
        picture_cloudinary: null,
      }),
    },

    reviews: {
      type: Array,
      default: () => [],
    },
  },

  data () {
    return {
      modal: false,
      completePages: false,
      showModal: false,
      mediaModal: null,
    }
  },

  computed: {
    ...mapState(useLocaleStore, ['isSwedish', 'locale']),

    video () {
      return this.info?.videoUrl || ''
    },

    name () {
      return this.info?.name
    },

    content () {
      return this.info?.info || null
    },

    reviewCount () {
      const videoCount = (this.info?.triptypes_videointroduction || []).length
      return videoCount < 2 ? 2 : (videoCount * 2) - 1
    },

    trips () {
      return this.info?.upcoming_triptypes || []
    },

    lectures () {
      return this.info?.triptypes_videointroduction || []
    },

    questions () {
      return this.info?.quick_questions || []
    },

    hasQuestions () {
      return this.questions.length > 0
    },

    hasReviews () {
      return (this.reviews || []).length > 0
    },

    reviewList () {
      const n = this.hasQuestions ? 2 : 6
      return (this.reviews || []).slice(0, n)
    },

    lectureColClass () {
      const n = this.lectures.length
      if (n === 1) {
        return ['sm:w-8/12', 'xl:w-6/12', 'm-auto']
      } else if (n === 3 || n > 5) {
        return ['sm:w-6/12', 'xl:w-4/12']
      }
      return ['sm:w-6/12', '3xl:w-3/12']
    },
  },

  methods: {
    showSingleVideo () {
      this.showModal = true
      this.mediaModal = {
        title: '',
        video: [{
          location: this.video,
          mediatype: 'video',
          type: 'internal',
        }],
      }
    },

    showMediaModal (video) {
      this.showModal = true
      this.mediaModal = {
        title: video?.name,
        video: [{
          location: video?.url,
          mediatype: 'video',
          type: 'url',
        }],
      }
    },

    hideMediaModal () {
      this.showModal = false
    },
  },
})
</script>

<style lang="scss">
.avatar-play-button,
.preview-play-button {
  width:80px !important;
  height:80px !important;
}
.avatar-play-button {
  opacity: .5;
}
.tourleader {
  &-intro {
    max-width: 465px;
    margin: auto;

    &__avatar {
      max-width: 250px;
    }
  }
  &-name {
    font-size: 1.75rem;
  }
  &-video {
    .image-content {
      &:before {
        position: absolute;
        top:0;
        left:0;
        width:100%;
        height:100%;
        display: block;
        content: "\a0";
        background: rgba(0,0,0,0.2);
      }
      .article-block--title {
        font-size: 18px;
        line-height: 1.25em;
      }
    }
  }
}
</style>
